import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import axios from "axios";
import "./GiftRedemptionPage.css";

// BASIC DEBUG - FILE LOADED
console.log("GiftRedemptionPage.js file is being loaded");
// Remove the alert as it might be blocking the component from loading properly

// Debug helper function to log with timestamp
const debugLog = (message, data = null) => {
  const timestamp = new Date().toISOString();
  const prefix = `[GIFT-DEBUG ${timestamp}]`;

  if (data) {
    console.log(prefix, message, data);
  } else {
    console.log(prefix, message);
  }
};

const GiftRedemptionPage = () => {
  const { code } = useParams();
  const { token, user, isAuthenticated, setPendingGift, clearPendingGift } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Log component initialization
  debugLog("GiftRedemptionPage initialized", {
    giftCode: code,
    isAuthenticated,
    hasToken: !!token,
    userId: user?.id,
    fromLoginRedirect: !!location?.state?.fromLogin,
    apiBaseUrl: process.env.REACT_APP_API_BASE_URL,
  });

  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(location?.state?.redeemed || false);

  // Track redemption attempts for fallback mechanism
  const [redemptionAttempts, setRedemptionAttempts] = useState(0);
  const [showManualRedemption, setShowManualRedemption] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  // Handle gift redemption - using useCallback to avoid dependency issues
  const handleRedeemGift = useCallback(async () => {
    debugLog("handleRedeemGift called", {
      isAuthenticated,
      hasToken: !!token,
      giftCode: code,
      userId: user?.id,
    });

    if (!isAuthenticated) {
      // Store the gift code in AuthContext for later redemption
      debugLog(
        "User not authenticated, storing gift code and redirecting to login"
      );
      setPendingGift(code);
      // Redirect to login with return URL
      navigate("/login", { state: { from: `/redeem/${code}` } });
      return;
    }

    try {
      setRedeeming(true);
      setError("");
      setRedemptionAttempts((prev) => prev + 1);

      // Collect debug info
      const debugData = {
        timestamp: new Date().toISOString(),
        userInfo: {
          isAuthenticated,
          hasToken: !!token,
          userId: user?.id,
          userEmail: user?.email,
        },
        giftCode: code,
        attemptNumber: redemptionAttempts + 1,
        fromLogin: !!location.state?.fromLogin,
        environment: {
          apiBaseUrl: process.env.REACT_APP_API_BASE_URL,
          nodeEnv: process.env.NODE_ENV,
        },
      };

      debugLog("Starting gift redemption attempt", debugData);
      setDebugInfo(debugData);

      // Get CSRF token from cookie
      debugLog("Checking for CSRF token in cookies");
      const allCookies = document.cookie;
      debugLog("All cookies", { cookies: allCookies });

      const csrfToken = document.cookie
        .split("; ")
        .find((row) => row.startsWith("XSRF-TOKEN="))
        ?.split("=")[1];

      debugLog("CSRF token extraction result", {
        found: !!csrfToken,
        tokenPrefix: csrfToken ? csrfToken.substring(0, 10) + "..." : "N/A",
      });

      // Create redemption request
      const requestData = {};
      debugLog("Request payload", { requestData });

      // Add authorization header and CSRF token
      const headers = {
        Authorization: `Bearer ${token}`,
        "X-CSRF-Token": csrfToken || "", // Include CSRF token if available
        "Content-Type": "application/json",
      };

      debugLog("Request headers prepared", { headers });
      debugData.requestHeaders = headers;

      // First, try to get a fresh CSRF token if needed
      if (!csrfToken) {
        debugLog("No CSRF token found in cookies, fetching fresh token");
        try {
          const csrfUrl = `${process.env.REACT_APP_API_BASE_URL}/csrf-token`;
          debugLog("Fetching CSRF token from", { url: csrfUrl });

          const startTime = Date.now();
          const csrfResponse = await axios.get(csrfUrl, {
            withCredentials: true,
          });
          const endTime = Date.now();

          debugLog("CSRF token response received", {
            status: csrfResponse.status,
            responseTime: `${endTime - startTime}ms`,
            headers: csrfResponse.headers,
            data: csrfResponse.data,
          });

          if (csrfResponse.data && csrfResponse.data.csrfToken) {
            headers["X-CSRF-Token"] = csrfResponse.data.csrfToken;
            debugLog("Fresh CSRF token obtained", {
              tokenPrefix: csrfResponse.data.csrfToken.substring(0, 10) + "...",
            });
            debugData.freshCsrfToken = csrfResponse.data.csrfToken;
          } else {
            debugLog(
              "CSRF token response did not contain a token",
              csrfResponse.data
            );
          }
        } catch (csrfError) {
          debugLog("Error fetching CSRF token", {
            error: csrfError.message,
            response: csrfError.response
              ? {
                  status: csrfError.response.status,
                  data: csrfError.response.data,
                }
              : null,
          });
          // Continue anyway, as CSRF might be bypassed in development
        }
      }

      // Prepare the redemption API call
      const redemptionUrl = `${process.env.REACT_APP_API_BASE_URL}/gifts/redeem/${code}`;
      debugLog("Preparing redemption API call", {
        url: redemptionUrl,
        method: "POST",
        headers,
        withCredentials: true,
      });

      // Make the redemption API call
      debugLog("Executing redemption API call");
      const startTime = Date.now();
      const response = await axios.post(redemptionUrl, requestData, {
        headers,
        withCredentials: true,
      });
      const endTime = Date.now();

      debugLog("Redemption API response received", {
        status: response.status,
        responseTime: `${endTime - startTime}ms`,
        headers: response.headers,
        data: response.data,
      });

      debugLog("Gift redemption successful");
      debugData.redemptionResponse = response.data;

      // Clear the pending gift code after successful redemption
      if (location.state?.fromLogin) {
        console.log("Clearing pending gift code after successful redemption");
        clearPendingGift();
      }

      setRedeemed(true);
      setShowManualRedemption(false);
    } catch (error) {
      debugLog("Error during gift redemption", {
        error: error.message,
        stack: error.stack,
        type: error.constructor.name,
      });

      // Update debug info with error details
      setDebugInfo((currentDebugInfo) => {
        if (!currentDebugInfo) return null;

        return {
          ...currentDebugInfo,
          error: {
            message: error.message,
            stack: error.stack,
            type: error.constructor.name,
            response: error.response
              ? {
                  status: error.response.status,
                  statusText: error.response.statusText,
                  data: error.response.data,
                  headers: error.response.headers,
                }
              : null,
            request: error.request ? { exists: true } : null,
          },
        };
      });

      // Enhanced error handling
      let errorMessage = "Failed to redeem gift. Please try again.";

      if (error.response) {
        debugLog("Error response details", {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers,
        });

        if (
          error.response.status === 403 &&
          error.response.data.error === "CSRF_ERROR"
        ) {
          errorMessage =
            "Security validation failed. Please refresh the page and try again.";
          debugLog("CSRF validation error detected");
        } else if (
          error.response.status === 400 &&
          error.response.data.message
        ) {
          errorMessage = error.response.data.message;
          debugLog("API returned error message", { message: errorMessage });
        } else if (error.response.status === 404) {
          errorMessage = "Gift not found or has been removed.";
          debugLog("Gift not found (404) error");
        } else if (error.response.status === 401) {
          errorMessage = "Your session has expired. Please log in again.";
          debugLog("Authentication error (401)");
        } else {
          errorMessage = error.response.data.message || errorMessage;
          debugLog("Other API error", {
            status: error.response.status,
            message: errorMessage,
          });
        }
      } else if (error.request) {
        // Request was made but no response received
        debugLog("No response received from server", {
          requestWasMade: true,
          networkError: true,
        });
        errorMessage =
          "Network error. Please check your connection and try again.";
      } else if (error.message) {
        // Something else happened while setting up the request
        debugLog("Request setup error", { message: error.message });
        errorMessage = error.message;
      }

      setError(errorMessage);
      debugLog("Error state set", { errorMessage });

      // Show manual redemption option after multiple failed attempts
      if (redemptionAttempts >= 1) {
        debugLog("Showing manual redemption option", {
          attempts: redemptionAttempts,
        });
        setShowManualRedemption(true);
      }
    } finally {
      setRedeeming(false);
    }
  }, [
    isAuthenticated,
    code,
    token,
    user,
    navigate,
    setPendingGift,
    clearPendingGift,
    location.state,
    redemptionAttempts,
    setRedeeming,
    setError,
    setRedeemed,
  ]);

  // Fetch gift details on component mount
  useEffect(() => {
    const fetchGiftDetails = async () => {
      debugLog("Starting gift details fetch", { giftCode: code });

      try {
        setLoading(true);

        // Log the API URL being called
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/gifts/code/${code}`;
        debugLog("Fetching gift details from API", { apiUrl });

        const startTime = Date.now();
        const response = await axios.get(apiUrl);
        const endTime = Date.now();

        debugLog("Gift details API response received", {
          status: response.status,
          responseTime: `${endTime - startTime}ms`,
          headers: response.headers,
        });

        if (response.data && response.data.gift) {
          debugLog("Gift details retrieved successfully", {
            gift: response.data.gift,
          });
          setGift(response.data.gift);

          // Log gift status for debugging
          debugLog("Gift status", {
            status: response.data.gift.status,
            giftType: response.data.gift.giftType,
            sessionCount: response.data.gift.sessionCount,
          });
        } else {
          debugLog("Gift details response missing gift data", response.data);
        }

        // Check if we should auto-redeem (coming from login with pending gift)
        if (location.state?.fromLogin && isAuthenticated && token) {
          debugLog("Auto-redemption triggered after login", {
            fromLogin: true,
            isAuthenticated,
            hasToken: !!token,
          });
          // We'll trigger the redemption after the gift details are loaded
          setTimeout(() => handleRedeemGift(), 500);
        }
      } catch (error) {
        debugLog("Error fetching gift details", {
          error: error.message,
          response: error.response
            ? {
                status: error.response.status,
                data: error.response.data,
              }
            : null,
        });

        console.error("Error fetching gift details:", error);
        setError(
          error.response?.data?.message ||
            "Failed to load gift details. Please try again."
        );
      } finally {
        setLoading(false);
        debugLog("Gift details fetch completed");
      }
    };

    if (code) {
      fetchGiftDetails();
    } else {
      debugLog("No gift code provided in URL params");
    }
  }, [code, location.state, isAuthenticated, token, handleRedeemGift]);

  // Determine session type for display
  const getSessionTypeDisplay = (giftType) => {
    return giftType?.includes("sauna") ? "Infrared Sauna" : "Red Light Therapy";
  };

  // Determine gift type for display
  const getGiftTypeDisplay = (giftType) => {
    return giftType?.includes("package") ? "Package" : "Drop-in Session";
  };

  // Render loading state
  if (loading) {
    return (
      <div className="gift-redemption-page">
        <div className="loading-spinner"></div>
        <p>Loading gift details...</p>
      </div>
    );
  }

  // Render error state
  if (error && !gift) {
    return (
      <div className="gift-redemption-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button
            onClick={() => navigate("/gift-cards")}
            className="primary-button"
          >
            Go to Gift Cards
          </button>
        </div>
      </div>
    );
  }

  // Render redeemed state
  if (redeemed) {
    return (
      <div className="gift-redemption-page">
        <div className="success-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M0 0h24v24H0V0z" fill="none" />
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm4.59-12.42L10 14.17l-2.59-2.58L6 13l4 4 8-8z" />
          </svg>
        </div>
        <h1>Gift Successfully Redeemed!</h1>
        <div className="redemption-details">
          <p className="success-message">
            {gift.sessionCount} {getSessionTypeDisplay(gift.giftType)} sessions
            have been added to your account.
          </p>
          <div className="gift-details-card">
            <h2>Gift Details</h2>
            <div className="gift-detail-item">
              <span className="detail-label">Session Type:</span>
              <span className="detail-value">
                {getSessionTypeDisplay(gift.giftType)}
              </span>
            </div>
            <div className="gift-detail-item">
              <span className="detail-label">Gift Type:</span>
              <span className="detail-value">
                {getGiftTypeDisplay(gift.giftType)}
              </span>
            </div>
            <div className="gift-detail-item">
              <span className="detail-label">Number of Sessions:</span>
              <span className="detail-value">{gift.sessionCount}</span>
            </div>
            <div className="gift-detail-item">
              <span className="detail-label">From:</span>
              <span className="detail-value">
                {gift.sender
                  ? `${gift.sender.firstName} ${gift.sender.lastName}`
                  : "A friend"}
              </span>
            </div>
            {gift.note && (
              <div className="gift-detail-item">
                <span className="detail-label">Note:</span>
                <p className="gift-note">{gift.note}</p>
              </div>
            )}
          </div>
          <div className="next-steps-info">
            <p>
              You can now book sessions using your gifted credits. Gifted
              credits will be used first when booking sessions.
            </p>
            <p>
              Your gifted credits will expire in 12 months. You will receive
              email notifications before they expire.
            </p>
          </div>
          <div className="redemption-actions">
            <button
              onClick={() => navigate("/book-session")}
              className="primary-button"
            >
              Book a Session Now
            </button>
            <button
              onClick={() => navigate("/member")}
              className="secondary-button"
            >
              Go to My Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render gift details and redemption button
  return (
    <div className="gift-redemption-page">
      <h1>Redeem Your Gift</h1>
      {gift && (
        <div className="redemption-details">
          <div className="gift-details-card">
            <h2>Gift Details</h2>
            <div className="gift-detail-item">
              <span className="detail-label">Session Type:</span>
              <span className="detail-value">
                {getSessionTypeDisplay(gift.giftType)}
              </span>
            </div>
            <div className="gift-detail-item">
              <span className="detail-label">Gift Type:</span>
              <span className="detail-value">
                {getGiftTypeDisplay(gift.giftType)}
              </span>
            </div>
            <div className="gift-detail-item">
              <span className="detail-label">Number of Sessions:</span>
              <span className="detail-value">{gift.sessionCount}</span>
            </div>
            <div className="gift-detail-item">
              <span className="detail-label">From:</span>
              <span className="detail-value">
                {gift.sender
                  ? `${gift.sender.firstName} ${gift.sender.lastName}`
                  : "A friend"}
              </span>
            </div>
            {gift.note && (
              <div className="gift-detail-item">
                <span className="detail-label">Note:</span>
                <p className="gift-note">{gift.note}</p>
              </div>
            )}
            <div className="gift-detail-item">
              <span className="detail-label">Status:</span>
              <span className={`gift-status ${gift.status}`}>
                {gift.status}
              </span>
            </div>
          </div>

          {(gift.status === "created" || gift.status === "paid") && (
            <div className="redemption-actions">
              <button
                onClick={handleRedeemGift}
                className="primary-button"
                disabled={redeeming}
              >
                {redeeming ? "Redeeming..." : "Redeem Gift"}
              </button>
              {!isAuthenticated && (
                <p className="login-note">
                  You'll need to log in or create an account to redeem this
                  gift.
                </p>
              )}
            </div>
          )}

          {gift.status === "redeemed" && (
            <div className="already-redeemed">
              <p>This gift has already been redeemed.</p>
              <button
                onClick={() => navigate("/member")}
                className="secondary-button"
              >
                Go to My Account
              </button>
            </div>
          )}

          {gift.status === "expired" && (
            <div className="expired-gift">
              <p>This gift has expired.</p>
              <button
                onClick={() => navigate("/gift-cards")}
                className="secondary-button"
              >
                Go to Gift Cards
              </button>
            </div>
          )}

          {gift.status === "regifted" && (
            <div className="regifted-gift">
              <p>This gift has been regifted to someone else.</p>
              <button
                onClick={() => navigate("/gift-cards")}
                className="secondary-button"
              >
                Go to Gift Cards
              </button>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}

              {showManualRedemption && (
                <div className="manual-redemption-section">
                  <h3>Manual Redemption Option</h3>
                  <p>
                    If you're experiencing issues with automatic redemption, you
                    can try the manual redemption process:
                  </p>
                  <ol>
                    <li>
                      Take a screenshot of this gift code:{" "}
                      <strong>{code}</strong>
                    </li>
                    <li>
                      Contact our support team at{" "}
                      <a href="mailto:support@divitiadora.com">
                        support@divitiadora.com
                      </a>
                    </li>
                    <li>
                      Include your screenshot and mention that you're having
                      trouble redeeming your gift
                    </li>
                  </ol>
                  <p>
                    Our team will manually add the credits to your account and
                    respond within 24 hours.
                  </p>

                  {debugInfo && (
                    <div className="debug-info">
                      <h4>Technical Details (for Support)</h4>
                      <details>
                        <summary>Click to show technical details</summary>
                        <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
                      </details>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GiftRedemptionPage;
